import { useState, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import MetaTags from '../components/MetaTags'
import BookingBanner from '../components/BookingBanner'
import ElfsightGoogleReviews from '../components/ElfsightGoogleReviews'

const CATEGORY_FILTERS = [
  'Alle',
  'Angst',
  'Rygestop',
  'Vægttab',
  'Børn & unge',
  'Skoleværing & børn',
  'Eksamensangst',
  'Stress & søvn',
  'Præstationsangst & følelser',
]

export default function ReferencesPage() {
  const [testimonials, setTestimonials] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedCategory, setSelectedCategory] = useState('Alle')
  const [searchQuery, setSearchQuery] = useState('')

  useEffect(() => {
    async function loadTestimonials() {
      try {
        setLoading(true)
        const res = await fetch('/_api/public/content/testimonials')
        if (res.ok) {
          const data = await res.json()
          const items = Array.isArray(data) ? data : data.items || []
          // sort by order
          items.sort((a, b) => (a.order || 99) - (b.order || 99))
          setTestimonials(items)
        }
      } catch (err) {
        console.error('Fejl ved hentning af referencer:', err)
      } finally {
        setLoading(false)
      }
    }
    loadTestimonials()
  }, [])

  const filtered = useMemo(() => {
    let list = [...testimonials]

    if (selectedCategory !== 'Alle') {
      const catLower = selectedCategory.toLowerCase()
      list = list.filter((t) => {
        const itemCat = (t.category || '').toLowerCase()
        return itemCat.includes(catLower)
      })
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim()
      list = list.filter(
        (t) =>
          t.name?.toLowerCase().includes(q) ||
          t.quote?.toLowerCase().includes(q) ||
          t.category?.toLowerCase().includes(q)
      )
    }

    return list
  }, [testimonials, selectedCategory, searchQuery])

  return (
    <>
      <MetaTags
        title="Referencer - Trine Rosendal"
        description="Læs hvad tidligere klienter fortæller om deres erfaringer og resultater med hypnose, hypnoterapi og healing hos Trine Rosendal i Holstebro."
        image="https://assets.auxodev.dk/assets/0c569b3d-b522-4d44-8ece-350bff7b17df/cat-1005-1-scaled.jpg"
        canonicalUrl="https://trine-rosendal.dk/referencer"
      />

      <div className="mx-auto max-w-6xl px-5 sm:px-8 py-8 sm:py-14 space-y-12">
        {/* Breadcrumbs */}
        <nav aria-label="Brødkrummer" className="flex items-center gap-2 text-xs text-[#5C6E52]">
          <Link to="/" className="hover:text-[#222E1D]">
            Forside
          </Link>
          <span>/</span>
          <span className="font-medium text-[#222E1D]">Referencer</span>
        </nav>

        {/* Header Introduction */}
        <div className="max-w-3xl space-y-4">
          <div className="inline-flex items-center gap-2 rounded-full border border-[#D6E2CB] bg-[#EEF3E8] px-3.5 py-1 text-xs font-medium text-[#4C5E43]">
            <i className="fa-solid fa-heart text-[11px] text-[#556B48]" aria-hidden="true" />
            <span>Klienternes egne ord</span>
          </div>

          <h1 className="font-serif text-3.5xl sm:text-4xl lg:text-5xl font-medium text-[#222E1D] tracking-tight">
            Referencer & kundeudtalelser
          </h1>

          <p className="text-base sm:text-lg text-[#4D5E46] leading-relaxed">
            Her kan du læse anmeldelser fra Google samt udtalelser fra børn, unge, forældre og voksne, som har været i behandling hos mig gennem årene.
          </p>
        </div>

        {/* Google Reviews Widget */}
        <div className="rounded-3xl border border-[#D6E2CB] bg-[#F7F9F3] p-6 sm:p-8 lg:p-10 shadow-xs space-y-4">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#5C7A4F]">
            <i className="fa-brands fa-google text-sm text-[#F5D154]" aria-hidden="true" />
            <span>Google Anmeldelser</span>
          </div>
          <ElfsightGoogleReviews />
        </div>

        {/* Filters & Search */}
        <div className="rounded-2xl border border-[#D6E2CB] bg-[#F7F9F3] p-4 sm:p-6 shadow-xs space-y-4">
          {/* Search bar */}
          <div className="relative max-w-md">
            <i
              className="fa-solid fa-magnifying-glass absolute left-3.5 top-1/2 -translate-y-1/2 text-xs text-[#556B48]"
              aria-hidden="true"
            />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Søg i udtalelser (f.eks. rygestop, angst, flyvning...)"
              className="w-full rounded-full border border-[#D6E2CB] bg-white py-2 pl-9 pr-4 text-sm text-[#222E1D] placeholder:text-[#8D9F85] focus:border-[#556B48] focus:outline-none focus:ring-1 focus:ring-[#556B48]"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-xs text-[#556B48] hover:text-[#222E1D]"
                aria-label="Ryd søgning"
              >
                <i className="fa-solid fa-xmark" aria-hidden="true" />
              </button>
            )}
          </div>

          {/* Category Tabs */}
          <div className="flex flex-wrap items-center gap-1.5 pt-2 border-t border-[#D6E2CB]">
            {CATEGORY_FILTERS.map((cat) => (
              <button
                key={cat}
                type="button"
                onClick={() => setSelectedCategory(cat)}
                className={`rounded-full px-3.5 py-1.5 text-xs font-medium transition cursor-pointer ${
                  selectedCategory === cat
                    ? 'bg-[#5C7A4F] text-white shadow-xs font-semibold'
                    : 'bg-[#EAF1E4] text-[#3D5234] hover:bg-[#D6E2CB]'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Testimonials List */}
        {loading ? (
          <div className="py-16 text-center text-sm text-[#556B48] animate-pulse">
            Indlæser udtalelser...
          </div>
        ) : filtered.length === 0 ? (
          <div className="rounded-2xl border border-[#DFE6D5] bg-[#F6F8F3] p-12 text-center text-[#685263] space-y-3">
            <p className="font-serif text-xl font-medium text-[#2B3824]">
              Ingen udtalelser matcher din søgning
            </p>
            <p className="text-sm">Prøv at nulstille dit filter for at se alle udtalelser.</p>
            <button
              type="button"
              onClick={() => {
                setSelectedCategory('Alle')
                setSearchQuery('')
              }}
              className="inline-flex items-center gap-1.5 rounded-full bg-[#E7EEDF] px-4 py-2 text-xs font-medium text-[#3E4E36]"
            >
              Nulstil filtre
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-7">
            {filtered.map((item) => (
              <article
                key={item.id || item.slug}
                className="flex flex-col justify-between rounded-3xl border border-[#DFE6D5] bg-[#F6F8F3] p-6 sm:p-8 shadow-xs hover:shadow-md transition-shadow"
              >
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="font-serif text-xl font-medium text-[#2B3824]">
                      {item.name}
                    </span>
                    {item.category && (
                      <span className="rounded-full bg-[#F3E9F1] px-3 py-0.5 text-xs font-medium text-[#684F63]">
                        {item.category}
                      </span>
                    )}
                  </div>

                  <div
                    className="prose prose-sm max-w-none text-[#33422E] leading-relaxed
                      prose-p:mb-3 prose-p:leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: item.quote }}
                  />
                </div>

                <div className="border-t border-[#E7EEDF] pt-4 mt-6 flex items-center justify-between text-xs text-[#7E6578]">
                  <span className="italic">Verificeret klientudtalelse</span>
                  <Link
                    to="/kontakt"
                    className="font-medium text-[#5C7A4F] hover:text-[#382333] underline"
                  >
                    Kontakt Trine
                  </Link>
                </div>
              </article>
            ))}
          </div>
        )}

        <BookingBanner />
      </div>
    </>
  )
}
